import React, { useEffect, useMemo, useState } from 'react';
import { useLang } from '../hooks/useLang';

const IR_LANGS = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'it', label: 'Italiano', short: 'IT' },
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'pt', label: 'Português', short: 'PT' }
];

const MAIN_LANGS = [
  { code: 'de', label: 'Deutsch', short: 'DE' },
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'it', label: 'Italiano', short: 'IT' }
];

function isIRContext() {
  if (typeof window === 'undefined') return false;
  const host = (window.location.hostname || '').toLowerCase();
  const path = (window.location.pathname || '').toLowerCase();
  const qp = new URLSearchParams(window.location.search);
  const forced = qp.get('ir');
  if (String(process.env.REACT_APP_FORCE_IR).toLowerCase() === 'true') return true;
  if (forced === '1' || forced === 'true') return true;
  const parts = host.split('.');
  const first = parts[0] || '';
  const second = parts[1] || '';
  const isIrSub = first === 'ir' || (first === 'www' && second === 'ir');
  const isIrPath = path === '/ir' || path.startsWith('/ir/');
  return isIrSub || isIrPath;
}

export default function LanguageSelector() {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);

  const list = useMemo(() => (isIRContext() ? IR_LANGS : MAIN_LANGS), []);

  const current = useMemo(() => list.find(l => l.code === lang) || list[0], [lang, list]);

  // Enforce allowed languages per context
  useEffect(() => {
    if (!list.some(l => l.code === lang)) {
      setLang(list[0].code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const toggle = () => setOpen(o => !o);
  const close = () => setOpen(false);

  const handleSelect = (code) => {
    setLang(code);
    close();
  };

  return (
    <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) close(); }}>
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200 transition"
      >
        <span className="font-medium">{current.short}</span>
        <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-50"
        >
          {list.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                role="option"
                aria-selected={lang === l.code}
                onClick={() => handleSelect(l.code)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${lang === l.code ? 'bg-gray-100 font-semibold' : ''}`}
              >
                <span>{l.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
